import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Collapsible from './Collapsible';
import Options from './Options';

const MultiSelect = (props) => {

    const {
        fields,
        onChange,
        type,
        componentConfig,
        filterName,
        disabled,
    } = props;

    const {
        highlighted,
        expandedLabel,
        collapsedLabel,
        columnCount,
    } = componentConfig;

    const {
        options,
        selected,
    } = useMemo(() => {

        return fields.reduce((acc, current) => {

            const options = current.options.map(option => ({
                ...option,
                fieldId: current.id,
            }));
            const selected = current.selected.map(item => ({
                ...item,
                fieldId: current.id,
            }));

            acc.options = acc.options.concat(options);
            acc.selected = acc.selected.concat(selected);

            return acc;

        }, {
            options: [],
            selected: [],
        });

    }, [fields]);

    const { visibleOptions, hiddenOptions } = useMemo(() => {

        let visibleOptions = [];
        let hiddenOptions = [];

        if(highlighted) {

            // If highlighted value is a number, then we can just pop off that amount from the options array
            if(typeof highlighted === 'number') {

                visibleOptions.push(...options.slice(0, highlighted));
                hiddenOptions.push(...options.slice(highlighted));

            } else {

                const optionsCopy = [...options];
                highlighted.forEach(element => {

                    visibleOptions.push(optionsCopy.find((opt, index) => {

                        if(opt.value === element) {

                            let temp = opt;
                            optionsCopy.splice(index, 1);
                            return temp;

                        }

                    }));

                });

                hiddenOptions = optionsCopy;

            }

        } else {

            visibleOptions = options;

        }

        return {
            visibleOptions,
            hiddenOptions,
        };

    }, [
        options,
        highlighted,
    ]);

    const onKeyDownHandler = (event) => {

        if (event.key === 'Enter') {

            event.target.checked = !event.target.checked;
            onClickHandler(event);

        }

    };

    const onClickHandler = (event) => {

        const value = event.target.value;
        const isSelect = event.target.checked;
        const fieldId = event.target.getAttribute('data-field-id');
        const nextSelectedItems = [...selected];
        const selectedItem = options.find(element => element.value.toString() === value.toString() && element.fieldId === fieldId);
        let selectEvent;

        if (isSelect) {

            nextSelectedItems.push(selectedItem);
            selectEvent = 'select-option';

        } else {

            const selectedIndex = selected.findIndex(element => element.value.toString() === value.toString() && element.fieldId === fieldId);
            nextSelectedItems.splice(selectedIndex, 1);
            selectEvent = 'deselect-option';

        }

        const data = {
            changes: selectedItem,
            selected: nextSelectedItems.filter(item => item.fieldId === fieldId),
            event: selectEvent,
        };

        const field = fields.find(field => field.id === fieldId);

        onChange(data, field);

    };

    return (
        <>
            <Options
                list={visibleOptions}
                type={type}
                onChange={onClickHandler}
                onKeyDown={onKeyDownHandler}
                selected={selected}
                columnCount={columnCount}
                disabled={disabled}
            />
            {hiddenOptions.length > 0 && (
                <Collapsible
                    collapsedLabel={collapsedLabel}
                    expandedLabel={expandedLabel}
                    collapsible={
                        <Options
                            list={hiddenOptions}
                            type={type}
                            onChange={onClickHandler}
                            onKeyDown={onKeyDownHandler}
                            selected={selected}
                            columnCount={columnCount}
                            disabled={disabled}
                        />
                    }
                    selectedNumber={
                        hiddenOptions.filter(opt => selected.some(element => element.value.toString() === opt.value.toString() && element.fieldId === opt.fieldId)).length
                    }
                    filterName={filterName}
                />
            )}
        </>
    );

};

MultiSelect.propTypes = {
    onChange: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            options: PropTypes.array.isRequired,
            selected: PropTypes.array.isRequired,
        }).isRequired
    ).isRequired,
    filterName: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    componentConfig: PropTypes.object,
    disabled: PropTypes.bool,
};

MultiSelect.defaultProps = {
    componentConfig: {},
    disabled: undefined,
};

export default MultiSelect;
